import type { EnrichedSlice, IntakeResult } from "./types.js";

/**
 * Format a single enriched slice as a markdown spec section.
 */
function formatSlice(slice: EnrichedSlice, index: number): string {
  const modelShort = slice.recommendedModel
    .replace("claude-", "")
    .replace(/-\d+-\d+$/, "");

  const lines: string[] = [
    `### ${index + 1}. ${slice.name}`,
    "",
    `**Complexity:** ${slice.complexity} | **Category:** ${slice.category} | **Priority:** ${slice.priority}`,
    "",
    `> ${slice.description}`,
    "",
  ];

  if (slice.rootCause) {
    lines.push(`**Root Cause:** ${slice.rootCause}`, "");
  }

  // Implementation tasks
  lines.push("#### Tasks");
  for (const task of slice.aiTasks) {
    lines.push(`- [ ] ${task}`);
  }
  lines.push("");

  // Acceptance criteria
  lines.push("#### Acceptance Criteria");
  // Split on periods or semicolons to create individual checkboxes
  const criteria = slice.acceptanceCriteria
    .split(/[.;]/)
    .map((c) => c.trim())
    .filter((c) => c.length > 0);
  for (const criterion of criteria) {
    lines.push(`- [ ] ${criterion}`);
  }
  lines.push("");

  // Estimate metadata
  lines.push("#### Estimate");
  lines.push(`- **Tokens:** ~${slice.estimatedTokens.toLocaleString()}`);
  lines.push(`- **Cost:** $${slice.estimatedCost.toFixed(2)}`);
  lines.push(`- **Recommended model:** ${modelShort}`);

  if (slice.velocityBenchmark) {
    const vb = slice.velocityBenchmark;
    lines.push(
      `- **Velocity benchmark:** ~${vb.avgSessions.toFixed(1)} sessions, ~${vb.avgDays.toFixed(1)} days (based on ${vb.dataPoints} similar tasks)`,
    );
  }

  return lines.join("\n");
}

/**
 * Format all slices as a full markdown spec document that an AI can build from.
 */
export function formatAsMarkdown(result: IntakeResult): string {
  const date = result.createdAt.slice(0, 10);

  const lines: string[] = [
    `# ${result.project} — Spec`,
    "",
    `Generated by Fathom on ${date}`,
    "",
    "## Summary",
    "",
    `| | |`,
    `|---|---|`,
    `| **Work items** | ${result.slices.length} |`,
    `| **Total tokens** | ~${result.totalEstimatedTokens.toLocaleString()} |`,
    `| **Total cost** | $${result.totalEstimatedCost.toFixed(2)} |`,
    `| **Source** | ${result.source} |`,
    "",
    "## Priority Order",
    "",
  ];

  // Priority summary table
  const p1 = result.slices.filter((s) => s.priority === "P0" || s.priority === "P1");
  const p2 = result.slices.filter((s) => s.priority === "P2");
  const p3 = result.slices.filter((s) => s.priority === "P3");

  if (p1.length > 0) {
    lines.push(`**Must-have (${p1.length}):** ${p1.map((s) => s.name).join(", ")}`);
  }
  if (p2.length > 0) {
    lines.push(`**Should-have (${p2.length}):** ${p2.map((s) => s.name).join(", ")}`);
  }
  if (p3.length > 0) {
    lines.push(`**Nice-to-have (${p3.length}):** ${p3.map((s) => s.name).join(", ")}`);
  }

  lines.push("", "---", "", "## Features", "");

  for (let i = 0; i < result.slices.length; i++) {
    lines.push(formatSlice(result.slices[i], i));
    lines.push("");
    if (i < result.slices.length - 1) {
      lines.push("---", "");
    }
  }

  return lines.join("\n");
}

/**
 * Format slices as a summary table (for CLI display).
 */
export function formatSliceTable(
  slices: EnrichedSlice[],
): Array<{
  name: string;
  category: string;
  complexity: string;
  priority: string;
  tokens: string;
  cost: string;
  model: string;
  benchmark: string;
}> {
  return slices.map((s) => ({
    name: s.name,
    category: s.category,
    complexity: s.complexity,
    priority: s.priority,
    tokens: s.estimatedTokens.toLocaleString(),
    cost: `$${s.estimatedCost.toFixed(2)}`,
    model: s.recommendedModel.replace("claude-", "").replace(/-\d+-\d+$/, ""),
    benchmark: s.velocityBenchmark
      ? `~${s.velocityBenchmark.avgDays.toFixed(1)}d / ${s.velocityBenchmark.avgSessions.toFixed(1)} sessions`
      : "—",
  }));
}
